import { Flex, Typography } from 'antd';
import { IconCalendar, IconClock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { OemPartnerGroupDto } from '@evimo/client/models';
import { SlaTypeDot } from '~/app/landing/sla/shared/components/SlaTypeDot';
import { SlaCurrencyLabel } from '~/app/landing/sla/shared/components/SlaCurrencyLabel';
import {
  SlaExpandedDetailGrid,
  type SlaExpandedDetailItem,
} from '~/app/landing/sla/shared/components/SlaExpandedDetailGrid';
import {
  formatCurrency,
  formatDateLabel,
} from '~/app/landing/sla/shared/utils/sla-format.utils';
import { SLA_RATE_WINDOW_UNIT_COLORS } from '~/app/landing/sla/shared/utils/sla-status.utils';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

type Props = {
  row: OemPartnerGroupDto;
};

export function OemPartnerExpandedRow({ row }: Props) {
  const { t } = useTranslation('sla');
  const contract = row.slaContract;

  if (!contract) {
    return (
      <div className="sla-table-expanded-panel">
        <Typography.Text type="secondary">
          {t('table.noContract')}
        </Typography.Text>
      </div>
    );
  }

  const validityItems: SlaExpandedDetailItem[] = [
    {
      key: 'validFrom',
      label: t('oems.fields.validFrom'),
      value: formatDateLabel(contract.validFrom),
    },
    {
      key: 'validTo',
      label: t('oems.fields.validTo'),
      value: contract.validTo
        ? formatDateLabel(contract.validTo)
        : t('review.openEnded'),
    },
    {
      key: 'rateCount',
      label: t('oems.columns.rates'),
      value: contract.rateEntries.length,
    },
  ];

  return (
    <div className="sla-table-expanded-panel">
      <Flex align="center" gap={8} className="sla-table-expanded-panel__title">
        <IconCalendar size={16} />
        <Typography.Text strong>{t('table.contractDetails')}</Typography.Text>
      </Flex>

      <SlaExpandedDetailGrid items={validityItems} />

      {contract.rateEntries.length > 0 ? (
        <>
          <Flex
            align="center"
            gap={8}
            className="sla-table-expanded-panel__subtitle"
          >
            <IconClock size={16} />
            <Typography.Text className="ds-section-header">
              {t('oems.rateMatrixTitle')}
            </Typography.Text>
          </Flex>
          <EvimoTable
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={contract.rateEntries}
            columns={[
              {
                title: t('oems.fields.windowValue'),
                key: 'window',
                render: (_, entry) => (
                  <Flex align="center" gap={8}>
                    <SlaTypeDot
                      color={
                        SLA_RATE_WINDOW_UNIT_COLORS[entry.windowUnit] ??
                        '#94a3b8'
                      }
                      size={8}
                    />
                    <span>
                      {entry.windowValue} {entry.windowUnit}
                    </span>
                  </Flex>
                ),
              },
              {
                title: t('oems.fields.cost'),
                key: 'cost',
                render: (_, entry) =>
                  formatCurrency(entry.costAmount, entry.currency),
              },
              {
                title: t('oems.fields.currency'),
                key: 'currency',
                render: (_, entry) => (
                  <SlaCurrencyLabel code={entry.currency} />
                ),
              },
            ]}
          />
        </>
      ) : null}
    </div>
  );
}
